// Centralized persistence for user onboarding selections
// Provides typed getters/setters and a reset function.

import { findNextParashaDate, parseDateString } from './parashaCalc';

export interface StoredParasha {
  english: string;
  hebrew: string;
}
export interface StoredTradition {
  key: string;
  length: string;
}
export interface StoredLearningCurve {
  key: string;
  versesCount: number | 'half' | 'all';
}

export interface StoredSelectedSection {
  type: 'aliyah' | 'haftara';
  aliyahIndex?: number;
}

// Readings the user chose to learn during onboarding (ChooseReadingsPage).
// `aliyah_<idx>` matches the aliyah index in the parasha's aliyot ranges (maftir is the last index).
export type LearningTargetKey = 'haftara' | `aliyah_${number}` | 'birchot_hatorah' | 'shema';

const KEYS = {
  parasha: 'selectedParasha',
  tradition: 'selectedTradition',
  rabbiId: 'selectedRabbiId',
  rabbi: 'selectedRabbi',
  haftaraRabbi: 'selectedHaftaraRabbi',
  learningCurve: 'selectedLearningCurve',
  aliyaLatoraDate: 'aliyaLatoraDate',
  aliyaLatoraDateUserSet: 'aliyaLatoraDateUserSet',
  userRole: 'userRole',
  selectedSection: 'selectedSection',
  learningTargets: 'selectedLearningTargets',
  rabbiConnected: 'rabbiConnectedViaCode',
} as const;

export type UserRole = 'rabbi' | 'student';

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function getParasha(): StoredParasha | null {
  return safeParse<StoredParasha | null>(localStorage.getItem(KEYS.parasha), null);
}
export function setParasha(p: StoredParasha) {
  localStorage.setItem(KEYS.parasha, JSON.stringify(p));
}

export function getTradition(): StoredTradition | null {
  return safeParse<StoredTradition | null>(localStorage.getItem(KEYS.tradition), null);
}
export function setTradition(t: StoredTradition) {
  localStorage.setItem(KEYS.tradition, JSON.stringify(t));
}

export function getLearningCurve(): StoredLearningCurve | null {
  return safeParse<StoredLearningCurve | null>(localStorage.getItem(KEYS.learningCurve), null);
}
export function setLearningCurve(lc: StoredLearningCurve) {
  localStorage.setItem(KEYS.learningCurve, JSON.stringify(lc));
}

export function getVersesPerGroup(): number | 'half' | 'all' {
  const lc = getLearningCurve();
  return lc?.versesCount ?? 1; // default to 1 pasuk
}

// Rabbi profiles are never persisted — details always come from the "rabbies" /
// "rabbiTraditionDefaults" collections (see useTraditionRabbi / getRabbiById in
// rabbiesFirestore.ts). Only the id of an explicit ChooseRabbiPage choice is
// stored. KEYS.rabbi / KEYS.haftaraRabbi remain listed so clearAllSelections
// wipes the profiles older versions stored.
export function getSelectedRabbiId(): string | null {
  return localStorage.getItem(KEYS.rabbiId);
}
export function setSelectedRabbiId(id: string) {
  localStorage.setItem(KEYS.rabbiId, id);
}

/**
 * True when the user connected to their rabbi via a connection code on
 * ConnectRabbiPage (as opposed to picking a default/optional rabbi later).
 */
export function setRabbiConnected(connected: boolean) {
  if (connected) {
    localStorage.setItem(KEYS.rabbiConnected, 'true');
  } else {
    localStorage.removeItem(KEYS.rabbiConnected);
  }
}

/** null = the user never made a choice (feature untouched); [] = explicitly chose nothing. */
export function getLearningTargets(): LearningTargetKey[] | null {
  const parsed = safeParse<unknown>(localStorage.getItem(KEYS.learningTargets), null);
  if (!Array.isArray(parsed)) return null;
  return parsed.filter((k): k is LearningTargetKey => typeof k === 'string');
}
export function setLearningTargets(targets: LearningTargetKey[]): void {
  localStorage.setItem(KEYS.learningTargets, JSON.stringify(targets));
}

export function getSelectedSection(): StoredSelectedSection | null {
  return safeParse<StoredSelectedSection | null>(localStorage.getItem(KEYS.selectedSection), null);
}
export function setSelectedSection(s: StoredSelectedSection): void {
  localStorage.setItem(KEYS.selectedSection, JSON.stringify(s));
}

/** Start of today in local time (for comparing dates). */
function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getAliyaLatoraDate(): string | null {
  const raw = localStorage.getItem(KEYS.aliyaLatoraDate);
  if (!raw) return null;
  const parsed = parseDateString(raw);
  // If the stored date is in the past, treat as not set and clear it so the date is "from now on"
  if (parsed && parsed < startOfToday()) {
    localStorage.removeItem(KEYS.aliyaLatoraDate);
    localStorage.removeItem(KEYS.aliyaLatoraDateUserSet);
    return null;
  }
  return raw;
}
/** True only when the user explicitly selected a date (e.g. Choose Date / Aliya La Tora page). When false or missing, target date is computed from next parasha or null. */
export function getAliyaLatoraDateUserSet(): boolean {
  return localStorage.getItem(KEYS.aliyaLatoraDateUserSet) === 'true';
}
export function setAliyaLatoraDateUserSet(userSet: boolean) {
  if (userSet) {
    localStorage.setItem(KEYS.aliyaLatoraDateUserSet, 'true');
  } else {
    localStorage.removeItem(KEYS.aliyaLatoraDateUserSet);
    localStorage.removeItem(KEYS.aliyaLatoraDate);
  }
}
export function setAliyaLatoraDate(date: string) {
  localStorage.setItem(KEYS.aliyaLatoraDate, date);
  localStorage.setItem(KEYS.aliyaLatoraDateUserSet, 'true');
}

/**
 * Returns the date to count down to:
 * - If the user explicitly set a date (aliya la tora), that date.
 * - If the user only selected a parasha (no specific date), the next time that parasha will be read from now.
 * - If no parasha selected, null (and any stale aliyaLatoraDate in storage is cleared).
 * When the target is not user-set, aliyaLatoraDate in localStorage is cleared so the stored value is null.
 */
export function getTargetDate(): Date | null {
  if (getAliyaLatoraDateUserSet() && getAliyaLatoraDate()) {
    const parsed = parseDateString(getAliyaLatoraDate()!);
    if (parsed) return parsed;
  }
  const parasha = getParasha();
  if (parasha) {
    if (!getAliyaLatoraDateUserSet()) {
      localStorage.removeItem(KEYS.aliyaLatoraDate);
    }
    return findNextParashaDate(parasha.english);
  }
  localStorage.removeItem(KEYS.aliyaLatoraDate);
  return null;
}

export function getUserRole(): UserRole | null {
  const role = localStorage.getItem(KEYS.userRole);
  if (role === 'rabbi' || role === 'student') return role;
  return null;
}
export function setUserRole(role: UserRole) {
  localStorage.setItem(KEYS.userRole, role);
}

export function clearAllSelections() {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
}

export function hasCompletedOnboarding(): boolean {
  return !!(getParasha() || getTradition() || getSelectedRabbiId());
}

// Track if user has passed the login/onboarding page
const HAS_PASSED_ONBOARDING_KEY = 'hasPassedOnboarding';

export function setPassedOnboarding(): void {
  localStorage.setItem(HAS_PASSED_ONBOARDING_KEY, 'true');
}

export function hasPassedOnboarding(): boolean {
  return localStorage.getItem(HAS_PASSED_ONBOARDING_KEY) === 'true';
}

export function clearPassedOnboarding(): void {
  localStorage.removeItem(HAS_PASSED_ONBOARDING_KEY);
}

// Rabbi is chosen later from the user portal, so it is not required to complete onboarding
export function hasAllSelections(): boolean {
  return !!(getParasha() && getTradition());
}

export function getLengthLimit(): number {
  const t = getTradition();
  switch (t?.length) {
    case 'short':
      return 10;
    case 'medium':
      return 20;
    case 'long':
      return 1000; // effectively all
    default:
      return 10;
  }
}
